import React from 'react';
import { useEventTracking } from 'react-event-tracker';
import { getDataLayerElements } from 'utils/getDataLayerElements';
import { ERROR_KIND_BOUNDARY_ERROR, useStoreValue } from 'store';
import { datadogRum } from '@datadog/browser-rum';
import { byCountry, getProductInfo } from 'utils';
import { NZErrors } from './NZErrors';
import { AUErrors } from './AUErrors';

export class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error) {
    if (this.props && this.props.onError) {
      this.props.onError(error);
    } else {
      let product;
      try {
        product = getProductInfo().name;
      } catch (e) {
        product = undefined;
      }
      const context = {
        kind: ERROR_KIND_BOUNDARY_ERROR,
        product,
      };
      datadogRum.addError(error, context);
    }
  }

  render() {
    if (this.state.hasError && import.meta.env.VITE_ENV !== 'dev') {
      return byCountry({
        AU: <AUErrors />,
        NZ: <NZErrors />,
      });
    }
    return this.props.children;
  }
}

export const ErrorBoundary = ({ children }) => {
  const [state] = useStoreValue();
  const { trackEvent } = useEventTracking();

  const handleOnError = error => {
    trackEvent({
      event: {
        category: 'application',
        action: 'application-error',
        location: window.location.pathname,
        label: 'Runtime application error',
      },
      ...getDataLayerElements({
        ...state,
        applicationErrors: error,
        applicationOutcome: 'Error',
      }),
    });
    let product;
    try {
      product = getProductInfo().name;
    } catch (e) {
      product = undefined;
    }
    const context = {
      kind: ERROR_KIND_BOUNDARY_ERROR,
      applicationId: state?.applicationId,
      product,
    };
    datadogRum.addError(error, context);

    if (import.meta.env.VITE_ENV !== 'prod') {
      // eslint-disable-next-line
      console.warn(error);
    }
  };

  return <Boundary onError={handleOnError}>{children}</Boundary>;
};
